import { TruckIcon } from '@heroicons/react/24/outline';
import type { PurchaseOrderLine } from '../../../services/purchaseOrders';
import { formatMoneyCents } from '../../../utils/money';
import { fmtPoDate } from './poFormat';

interface PurchaseOrderLinesProps {
    lines: PurchaseOrderLine[];
    canEdit: boolean;
    onMarkShipped: (line: PurchaseOrderLine) => void;
}

function lineExpectedCents(line: PurchaseOrderLine): number | null {
    return line.expectedUnitCostCents === null ? null : line.expectedUnitCostCents * line.quantity;
}

function ShippingState({ line }: { line: PurchaseOrderLine }) {
    if (line.canceledAt) {
        return <span className="text-xs text-gray-400">Canceled {fmtPoDate(line.canceledAt)}</span>;
    }
    if (line.shippedQuantity <= 0) {
        return <span className="text-xs text-gray-400">Not shipped</span>;
    }
    const partial = line.shippedQuantity < line.quantity;
    return (
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
            <span className={partial ? 'text-amber-600 font-medium' : 'text-emerald-600'}>
                {line.shippedQuantity} of {line.quantity} shipped
            </span>
            {line.shippedAt && <span className="text-gray-400">{fmtPoDate(line.shippedAt)}</span>}
            {line.trackingNumber && (
                <span className="font-mono text-[11px] text-gray-500" title={line.trackingNumber}>
                    {line.trackingNumber}
                </span>
            )}
        </span>
    );
}

export function PurchaseOrderLines({ lines, canEdit, onMarkShipped }: PurchaseOrderLinesProps) {
    if (lines.length === 0) {
        return <p className="text-sm text-gray-400">No lines on this purchase order.</p>;
    }

    return (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            {lines.map((line) => {
                const expected = lineExpectedCents(line);
                return (
                    <div key={line.id} className="flex flex-col gap-1.5 px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p
                                    className="text-sm font-medium text-gray-800 truncate"
                                    title={line.productName}
                                >
                                    {line.productName}
                                </p>
                                {line.description && (
                                    <p
                                        className="text-xs text-gray-500 truncate"
                                        title={line.description}
                                    >
                                        {line.description}
                                    </p>
                                )}
                            </div>
                            <div className="text-right shrink-0">
                                <p className="text-sm text-gray-700 tabular-nums">
                                    ×{line.quantity}
                                </p>
                                {expected === null ? (
                                    <p className="text-[11px] text-amber-600">No vendor cost</p>
                                ) : (
                                    <p className="text-[11px] text-gray-500 tabular-nums">
                                        {formatMoneyCents(expected)}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                            <ShippingState line={line} />
                            {canEdit && !line.canceledAt && (
                                <button
                                    type="button"
                                    onClick={() => onMarkShipped(line)}
                                    className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900 transition-colors shrink-0"
                                >
                                    <TruckIcon className="h-3.5 w-3.5" />
                                    {line.shippedQuantity > 0 ? 'Update shipping' : 'Mark shipped'}
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
